// /onboarding — first-run guided setup. A focused three-step wizard
// (Bienvenida -> WhatsApp -> Todo listo) the owner of a new tenant sees before the
// assistant is live. Concierge tone: the account manager is named and present on
// every step. Rendered as its own focused surface (single task, no distractions)
// rather than inside the full shell. Data via the getOnboarding() seam, wired to
// the real API.

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Fragment, type ReactNode, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  activateAssistant,
  getOnboarding,
  type OnboardingData,
  type SetupItem,
  type SummaryRow,
} from './onboarding-data.js'
import { Button, Spinner, toast, Toaster } from '../../design-system/index.js'

const STEPS = [
  { id: 'welcome', label: 'Bienvenida' },
  { id: 'whatsapp', label: 'WhatsApp' },
  { id: 'golive', label: 'Todo listo' },
]

function CheckIcon({ className = 'size-[14px]' }: { className?: string }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className={className}
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  )
}

function Chevron({ dir = 'right' }: { dir?: 'left' | 'right' }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="size-3.5"
    >
      <path d={dir === 'left' ? 'm15 18-6-6 6-6' : 'm9 6 6 6-6 6'} />
    </svg>
  )
}

const SETUP_ICON: Record<SetupItem['id'], ReactNode> = {
  whatsapp: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Z" />,
  assistant: (
    <>
      <circle cx="12" cy="8" r="3.6" />
      <path d="M5 20c0-3.4 3-5.4 7-5.4s7 2 7 5.4" />
    </>
  ),
  golive: (
    <>
      <path d="m5 11 4 4 11-11" />
      <path d="M3 17l4 4 14-14" />
    </>
  ),
}

const SETUP_TONE: Record<SetupItem['tone'], string> = {
  blue: 'bg-brand-blue/10 text-link',
  ink: 'bg-[color-mix(in_srgb,var(--accent-ink)_10%,transparent)] text-accent-ink',
  ok: 'bg-ok-bg text-ok',
}

function Stepper({ current }: { current: number }): JSX.Element {
  return (
    <ol className="mb-6 flex items-start justify-center" aria-label="Progreso de configuración">
      {STEPS.map((s, i) => {
        const done = i < current
        const active = i === current
        return (
          <Fragment key={s.id}>
            {i > 0 && (
              <span
                aria-hidden
                className={`mt-[15px] h-0.5 w-[54px] rounded-full ${i <= current ? 'bg-ok' : 'bg-line'}`}
              />
            )}
            <li
              className="flex w-24 flex-col items-center gap-2"
              aria-current={active ? 'step' : undefined}
            >
              <span
                aria-hidden
                className={`grid size-[30px] place-items-center rounded-full text-[12px] font-bold ${
                  done
                    ? 'bg-ok text-white'
                    : active
                      ? 'bg-brand-blue text-white shadow-[0_0_0_4px_color-mix(in_srgb,var(--brand-blue)_17%,transparent)]'
                      : 'border-[1.7px] border-line text-text-3'
                }`}
              >
                {done ? <CheckIcon /> : i + 1}
              </span>
              <span
                className={`text-center text-[11px] leading-tight ${active ? 'font-bold text-text' : 'font-semibold text-text-3'}`}
              >
                <span className="sr-only">
                  {done ? 'Completado: ' : active ? 'Paso actual: ' : 'Pendiente: '}
                </span>
                {s.label}
              </span>
            </li>
          </Fragment>
        )
      })}
    </ol>
  )
}

function Hero({
  badge,
  title,
  children,
}: {
  badge: ReactNode
  title: string
  children: ReactNode
}): JSX.Element {
  return (
    <div className="px-6 pb-1 pt-6 text-center">
      <span
        aria-hidden
        className="mx-auto mb-3.5 grid size-[54px] place-items-center rounded-2xl bg-[image:var(--brand-gradient)] text-white shadow-[0_8px_22px_-8px_rgba(37,99,235,.55)]"
      >
        {badge}
      </span>
      <h2 className="text-[20px] font-extrabold tracking-[-0.027em] text-text">{title}</h2>
      <p className="mx-auto mt-2 max-w-[42ch] text-[13.5px] leading-relaxed text-text-2">
        {children}
      </p>
    </div>
  )
}

function ConciergeNote({
  initials,
  name,
  children,
}: {
  initials: string
  name: string
  children: ReactNode
}): JSX.Element {
  return (
    <div className="mt-4 flex items-start gap-3 rounded-xl border border-brand-blue/20 bg-brand-blue/[0.05] p-3.5">
      <span
        aria-hidden
        className="grid size-[34px] flex-none place-items-center rounded-full bg-[image:var(--brand-gradient)] text-[12px] font-bold text-white"
      >
        {initials}
      </span>
      <p className="min-w-0 text-[11.5px] leading-relaxed text-text-2">
        <span className="block text-[12px] font-bold text-text">{name}</span>
        {children}
      </p>
    </div>
  )
}

function SetupRow({ item, index }: { item: SetupItem; index: number }): JSX.Element {
  return (
    <li className="flex items-center gap-3 rounded-xl border border-line bg-surface-2 px-3.5 py-3">
      <span
        aria-hidden
        className={`grid size-[34px] flex-none place-items-center rounded-[10px] ${SETUP_TONE[item.tone]}`}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
          className="size-[17px]"
        >
          {SETUP_ICON[item.id]}
        </svg>
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[13px] font-semibold text-text">{item.title}</span>
        <span className="mt-0.5 block text-[11.5px] leading-snug text-text-2">
          {item.description}
        </span>
      </span>
      <span
        aria-hidden
        className="grid size-[22px] flex-none place-items-center rounded-full border border-line bg-hover text-[11px] font-bold text-text-3"
      >
        {index + 1}
      </span>
    </li>
  )
}

function SummaryRowView({ row }: { row: SummaryRow }): JSX.Element {
  return (
    <div className="flex items-center gap-3 border-b border-line px-4 py-3 last:border-b-0">
      <span className="w-32 flex-none text-[12px] text-text-2">{row.label}</span>
      <span className="flex min-w-0 flex-1 items-center justify-end gap-1.5 text-right text-[13px] font-semibold text-text">
        {row.connected && <CheckIcon className="size-3 flex-none text-ok" />}
        <span className="truncate tabular-nums">{row.value}</span>
      </span>
    </div>
  )
}

function StepWelcome({ data, onNext }: { data: OnboardingData; onNext: () => void }): JSX.Element {
  return (
    <>
      <Hero
        badge={
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
            className="size-[26px]"
          >
            <path d="M5 13l4 4L19 7" />
            <path d="M3 8.5 4.5 10M21 8.5 19.5 10" />
          </svg>
        }
        title={`Te damos la bienvenida a INHERTA, ${data.ownerName}`}
      >
        En los próximos minutos dejamos listo tu agente de WhatsApp. Son tres pasos cortos y no
        tienes que saber nada técnico.
      </Hero>
      <div className="px-6 pb-5 pt-4">
        <ul className="flex flex-col gap-2.5">
          {data.setupItems.map((item, i) => (
            <SetupRow key={item.id} item={item} index={i} />
          ))}
        </ul>
        <ConciergeNote
          initials={data.manager.initials}
          name={`${data.manager.name}, tu gerente de cuenta`}
        >
          Yo te acompaño en todo el proceso. Si algo no queda claro, escríbeme y lo vemos juntas.{' '}
          <b className="font-semibold text-text">Nunca estás sola.</b>
        </ConciergeNote>
      </div>
      <div className="flex items-center border-t border-line bg-surface-2 px-6 py-4">
        <Button className="ml-auto" iconEnd={<Chevron />} onClick={onNext}>
          Comenzar
        </Button>
      </div>
    </>
  )
}

function StepWhatsApp({
  data,
  onBack,
  onNext,
}: {
  data: OnboardingData
  onBack: () => void
  onNext: () => void
}): JSX.Element {
  return (
    <>
      <Hero
        badge={
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.9"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
            className="size-[26px]"
          >
            <path d="M12 3a8.6 8.6 0 0 0-7.4 12.9L3.5 21l5.3-1.1A8.6 8.6 0 1 0 12 3Z" />
          </svg>
        }
        title="Conectemos tu WhatsApp"
      >
        Este es el número de WhatsApp Business por el que tus pacientes te van a escribir. Ya quedó
        verificado con Meta.
      </Hero>
      <div className="px-6 pb-5 pt-4">
        <div className="overflow-hidden rounded-xl border border-line bg-surface-2">
          <div className="flex items-center gap-3 p-4">
            <span
              aria-hidden
              className="grid size-11 flex-none place-items-center rounded-xl bg-ok-bg text-ok [&_svg]:size-[22px]"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.9"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M12 3a8.6 8.6 0 0 0-7.4 12.9L3.5 21l5.3-1.1A8.6 8.6 0 1 0 12 3Z" />
              </svg>
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-[11px] font-semibold uppercase tracking-[0.04em] text-text-3">
                Número conectado
              </span>
              <span className="mt-0.5 block text-[16px] font-bold tabular-nums text-text">
                {data.whatsapp.number}
              </span>
            </span>
            <span className="inline-flex flex-none items-center gap-1.5 rounded-pill bg-ok-bg px-2.5 py-1 text-[11.5px] font-bold text-ok">
              <CheckIcon className="size-3" />
              Conectado
            </span>
          </div>
          <div className="flex items-start gap-2.5 border-t border-line px-4 py-3">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
              className="mt-px size-[15px] flex-none text-ok"
            >
              <path d="M12 2 4 5.5v6c0 5 3.4 8.7 8 10 4.6-1.3 8-5 8-10v-6L12 2Z" />
              <path d="m9 12 2 2 4-4" />
            </svg>
            <span className="text-[11.5px] leading-relaxed text-text-2">
              <b className="font-semibold text-text">Verificado y listo.</b>{' '}
              {data.whatsapp.verifiedNote}
            </span>
          </div>
        </div>
        <ConciergeNote initials={data.manager.initials} name="De parte de tu gerente de cuenta">
          La parte técnica de WhatsApp y Meta{' '}
          <b className="font-semibold text-text">la hicimos nosotros</b>. Si más adelante quieres
          cambiar de número o sumar otro, escríbeme y lo gestiono yo.
        </ConciergeNote>
      </div>
      <div className="flex items-center border-t border-line bg-surface-2 px-6 py-4">
        <Button variant="secondary" iconStart={<Chevron dir="left" />} onClick={onBack}>
          Atrás
        </Button>
        <Button className="ml-auto" iconEnd={<Chevron />} onClick={onNext}>
          Continuar
        </Button>
      </div>
    </>
  )
}

function StepGoLive({
  data,
  onBack,
  onActivate,
  onLater,
  activating,
}: {
  data: OnboardingData
  onBack: () => void
  onActivate: () => void
  onLater: () => void
  activating: boolean
}): JSX.Element {
  return (
    <>
      <Hero
        badge={
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.9"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
            className="size-[26px]"
          >
            <path d="m5 11 4 4 11-11" />
            <path d="M3 17l4 4 14-14" />
          </svg>
        }
        title="Todo quedó listo de nuestro lado"
      >
        Esto es lo que dejamos preparado. Revísalo con calma; tu equipo INHERTA publica tu agente en
        cuanto tu cuenta esté confirmada.
      </Hero>
      <div className="px-6 pb-5 pt-4">
        <div className="overflow-hidden rounded-xl border border-line bg-surface">
          {data.summary.map((row) => (
            <SummaryRowView key={row.id} row={row} />
          ))}
        </div>
        <div className="mt-4 flex items-start gap-2.5 text-[12px] leading-relaxed text-text-2">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.1"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
            className="mt-px size-4 flex-none text-ok"
          >
            <circle cx="12" cy="12" r="9.5" />
            <path d="M12 11v5M12 7.5h.01" />
          </svg>
          <span>
            Todo esto se puede ajustar después, las veces que quieras, desde{' '}
            <b className="font-semibold text-text">Agentes</b> (horario, estilo, conocimiento) o con
            tu equipo INHERTA. Si algo no te gusta, lo cambiamos contigo.
          </span>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3 border-t border-line bg-surface-2 px-6 py-4">
        <Button variant="secondary" iconStart={<Chevron dir="left" />} onClick={onBack}>
          Atrás
        </Button>
        <button
          type="button"
          onClick={onLater}
          className="ml-auto rounded text-[12px] font-semibold text-text-3 transition-colors hover:text-text-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-blue"
        >
          Ver después
        </button>
        <Button
          loading={activating}
          onClick={onActivate}
          iconStart={
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
              className="size-[15px]"
            >
              <path d="M13 2 4.5 13H11l-1 9 8.5-11H12l1-9Z" />
            </svg>
          }
        >
          Terminar primeros pasos
        </Button>
      </div>
    </>
  )
}

export function OnboardingPage(): JSX.Element {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['onboarding'], queryFn: getOnboarding })
  const [step, setStep] = useState(0)
  const [activating, setActivating] = useState(false)

  /**
   * Leave the wizard for good.
   *
   * DROPPING the cached ['home'] entry is the whole fix for the loop an
   * Administrador hit on a tenant that had been in daily use. Home redirects
   * here whenever its `onboardingPending` is true, and with `staleTime: 10s` a
   * finished wizard navigated back to a Home that still held the PRE-activation
   * payload — so it redirected straight back, and the wizard's step is plain
   * useState(0), so every bounce restarted at step one. Pressing "next" forever
   * could never win; only "Ver después" escaped, because it sets the
   * sessionStorage flag Home checks before redirecting.
   *
   * removeQueries, not invalidateQueries: ['home'] is INACTIVE while we stand
   * here, so invalidation only marks it stale and Home would still render one
   * frame from the stale cache — which is all `<Navigate>` needs. Removing it
   * makes Home mount with no data at all, show its spinner, and decide on the
   * server's fresh answer.
   */
  function leaveWizard(): void {
    qc.removeQueries({ queryKey: ['home'] })
    void navigate('/home')
  }

  // The wizard must not render for a workspace that is past first-run. Reaching
  // /onboarding by URL (the route has no guard) used to serve a full flow whose
  // finish button could not change anything.
  // Keyed on `data` alone: leaveWizard closes over qc and navigate, both stable
  // for the life of the page, and re-running this on their identity would only
  // risk a double navigation.
  useEffect(() => {
    if (data && !data.pending) leaveWizard()
  }, [data])

  async function onActivate(): Promise<void> {
    setActivating(true)
    try {
      await activateAssistant()
      leaveWizard()
    } catch (err) {
      // Surface what actually failed. A bare `catch {}` with a fixed sentence
      // is how a silent loop stays invisible: the user pressed the button, saw
      // the same screen, and had nothing to report.
      const res = (
        err as { response?: { status?: number; data?: { error?: { message?: string } } } }
      ).response
      // Already stamped is success, not a failure — the flag write is
      // idempotent server-side, but a proxy or a double-click can still surface
      // a conflict, and bouncing the user back for it would rebuild the loop.
      if (res?.status === 409) {
        leaveWizard()
        return
      }
      toast.error('No pudimos guardar tu confirmación', {
        description:
          res?.data?.error?.message ?? 'Inténtalo de nuevo o escríbele a tu equipo INHERTA.',
      })
    } finally {
      setActivating(false)
    }
  }

  return (
    <div className="flex min-h-dvh flex-col bg-bg">
      <header className="flex h-14 flex-none items-center gap-3 border-b border-line bg-surface px-5">
        <span className="text-[13px] font-semibold text-text">Primeros pasos</span>
        <button
          type="button"
          onClick={() => void navigate('/mensajes')}
          className="ml-auto inline-flex items-center gap-1.5 rounded text-[12.5px] font-semibold text-text-2 transition-colors hover:text-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-blue"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.1"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
            className="size-3.5 text-text-3"
          >
            <circle cx="12" cy="12" r="9.5" />
            <path d="M9.2 9.3a2.9 2.9 0 0 1 5.6 1c0 1.9-2.8 2.5-2.8 2.5M12 17h.01" />
          </svg>
          Necesito ayuda
        </button>
      </header>

      <main className="flex flex-1 items-start justify-center overflow-auto px-5 py-7">
        {/* `!data.pending` keeps the wizard off screen entirely while the effect
            above navigates away, rather than flashing a first-run flow at
            someone whose workspace is already live. */}
        {isLoading || !data || !data.pending ? (
          <div className="py-10">
            <Spinner label="Cargando tus primeros pasos…" />
          </div>
        ) : (
          <div data-testid="onboarding" className="w-full max-w-[560px]">
            <Stepper current={step} />
            <div className="overflow-hidden rounded-2xl border border-line bg-surface shadow-card">
              {step === 0 && <StepWelcome data={data} onNext={() => setStep(1)} />}
              {step === 1 && (
                <StepWhatsApp data={data} onBack={() => setStep(0)} onNext={() => setStep(2)} />
              )}
              {step === 2 && (
                <StepGoLive
                  data={data}
                  onBack={() => setStep(1)}
                  onActivate={() => void onActivate()}
                  onLater={() => {
                    // Home stops redirecting here for the rest of the
                    // session; its banner becomes the re-entry point.
                    sessionStorage.setItem('onboarding-skipped', '1')
                    leaveWizard()
                  }}
                  activating={activating}
                />
              )}
            </div>
          </div>
        )}
      </main>
      <Toaster />
    </div>
  )
}
